import React, { useState, useEffect, useCallback } from "react";
import { authFetch, API_BASE } from "../api";
import "./ApiKeysEditor.css";

export default function ApiKeysEditor({ integrationName, onClose }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchKeys = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/secrets/${integrationName}`);
      const data = await res.json();
      setKeys(data.keys || []);
    } catch {
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, [integrationName]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const addKey = async () => {
    if (!newKey.trim() || !newValue.trim()) return;
    setSaving(true);
    try {
      await authFetch(`${API_BASE}/secrets/${integrationName}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secrets: { [newKey.trim()]: newValue.trim() } }),
      });
      setKeys((prev) => [...new Set([...prev, newKey.trim()])]);
      setNewKey("");
      setNewValue("");
    } catch (err) {
      console.error("Failed to save:", err);
    } finally {
      setSaving(false);
    }
  };

  const removeKey = async (keyName) => {
    try {
      await authFetch(`${API_BASE}/secrets/${integrationName}/${keyName}`, { method: "DELETE" });
      setKeys((prev) => prev.filter((k) => k !== keyName));
    } catch { /* ignore */ }
  };

  return (
    <div className="ake-overlay">
      <div className="ake-modal">
        <div className="ake-header">
          <h2>🔑 API Keys: {integrationName}</h2>
          <button className="ake-close" onClick={onClose}>&times;</button>
        </div>
        <p className="ake-desc">
          Add the API keys your app needs. Keys are encrypted and never appear in the generated code.
          Use the exact key names shown in the chat instructions.
        </p>

        {loading ? (
          <div className="ake-loading">Loading...</div>
        ) : (
          <>
            {keys.length > 0 && (
              <div className="ake-list">
                {keys.map((k) => (
                  <div key={k} className="ake-row">
                    <span className="ake-key-name">{k}</span>
                    <div className="ake-row-actions">
                      <span className="ake-configured">configured</span>
                      <button className="ake-update" onClick={() => { setNewKey(k); setNewValue(""); }}>Update</button>
                      <button className="ake-remove" onClick={() => removeKey(k)}>&times;</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="ake-form">
              <div className="ake-form-row">
                <label className="ake-field">
                  <span>Name</span>
                  <input
                    type="text"
                    placeholder="e.g. NEWS_API_KEY"
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ""))}
                  />
                </label>
                <label className="ake-field ake-field-wide">
                  <span>Value</span>
                  <input
                    type="password"
                    placeholder="Paste your key here"
                    value={newValue}
                    autoComplete="off"
                    onChange={(e) => setNewValue(e.target.value)}
                  />
                </label>
              </div>
              <button
                className="ake-save"
                onClick={addKey}
                disabled={saving || !newKey || !newValue}
              >
                {saving ? "Saving..." : keys.includes(newKey) ? "Update Key" : "Add Key"}
              </button>
            </div>

            <div className="ake-tip">
              After adding your keys, reload the preview to see data appear.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
